<template>
  <Teleport to="head">
    <component :is="'style'">
      @page { size: {{ paperSize === 'A5' ? 'A5' : 'A4' }}; margin: 0; }
    </component>
  </Teleport>
  <div v-if="invoiceData" :class="['cash-memo-print', `paper-${paperSize.toLowerCase()}`]">
    <!-- Header -->
    <div class="invoice-header">
      <h1 class="invoice-title">CASH MEMO</h1>
      <h2 class="invoice-title-tamil">பண ரசீது</h2>
    </div>

    <!-- Business Details -->
    <div class="business-details">
      <h3>{{ invoiceData.business_name }}</h3>
      <p v-if="invoiceData.business_address">{{ invoiceData.business_address }}</p>
      <p v-if="invoiceData.business_phone">
        <strong>Phone:</strong> {{ invoiceData.business_phone }}
      </p>
      <p v-if="invoiceData.business_state">
        <strong>State:</strong> {{ invoiceData.business_state }}
      </p>
    </div>

    <!-- Invoice Info -->
    <div class="invoice-info">
      <div class="invoice-info-row">
        <span><strong>Bill No:</strong> {{ invoiceData.invoice_number }}</span>
        <span><strong>Date:</strong> {{ formatDate(invoiceData.invoice_date) }}</span>
      </div>
      <div v-if="invoiceData.customer_name" class="invoice-info-row">
        <span><strong>Customer:</strong> {{ invoiceData.customer_name }}</span>
        <span v-if="invoiceData.customer_phone">
          <strong>Phone:</strong> {{ invoiceData.customer_phone }}
        </span>
      </div>
      <div v-if="invoiceData.payment_mode" class="invoice-info-row">
        <span><strong>Payment Mode:</strong> {{ formatPaymentMode(invoiceData.payment_mode) }}</span>
      </div>
    </div>

    <!-- Line Items Table -->
    <table class="items-table">
      <thead>
        <tr>
          <th class="text-center" style="width: 40px">S.No</th>
          <th style="width: 350px">Particulars / விவரங்கள்</th>
          <th class="text-center" style="width: 80px">Qty</th>
          <th class="text-right" style="width: 100px">Rate</th>
          <th v-if="hasDiscounts" class="text-right" style="width: 80px">Disc</th>
          <th class="text-right" style="width: 120px">Amount</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(item, index) in invoiceData.items" :key="index">
          <td class="text-center">{{ index + 1 }}</td>
          <td>
            <div class="product-name">{{ item.product_name }}</div>
            <div v-if="item.product_name_tamil" class="tamil-name">
              {{ item.product_name_tamil }}
            </div>
          </td>
          <td class="text-center">{{ item.quantity }}</td>
          <td class="text-right">₹{{ item.unit_price.toFixed(2) }}</td>
          <td v-if="hasDiscounts" class="text-right">{{ getItemDiscountDisplay(item) }}</td>
          <td class="text-right">₹{{ calculateLineTotal(item).toFixed(2) }}</td>
        </tr>
        <!-- Empty rows for spacing (if needed) -->
        <tr v-for="i in emptyRowsCount" :key="'empty-' + i" class="empty-row">
          <td>&nbsp;</td>
          <td>&nbsp;</td>
          <td>&nbsp;</td>
          <td>&nbsp;</td>
          <td v-if="hasDiscounts">&nbsp;</td>
          <td>&nbsp;</td>
        </tr>
      </tbody>
    </table>

    <!-- Totals Section -->
    <div class="totals-section">
      <div class="totals-left">
        <div class="amount-in-words">
          <strong>Amount in Words:</strong><br />
          {{ amountInWords }}
        </div>

        <!-- Loyalty Points Earned -->
        <div v-if="invoiceData.loyalty_points_earned > 0" class="loyalty-info">
          <strong>Loyalty Points Earned:</strong> {{ invoiceData.loyalty_points_earned.toFixed(0) }} points
        </div>
      </div>

      <div class="totals-right">
        <div class="total-row subtotal">
          <span>Subtotal:</span>
          <span>{{ subtotal.toFixed(2) }}</span>
        </div>
        <div v-if="invoiceData.discount_amount > 0" class="total-row discount">
          <span>Discount:</span>
          <span>-{{ invoiceData.discount_amount.toFixed(2) }}</span>
        </div>
        <div v-if="invoiceData.loyalty_discount_amount > 0" class="total-row discount">
          <span>Loyalty Discount:</span>
          <span>-{{ invoiceData.loyalty_discount_amount.toFixed(2) }}</span>
        </div>
        <div class="total-row grand-total">
          <span><strong>Total Amount:</strong></span>
          <span><strong>{{ grandTotal.toFixed(2) }}</strong></span>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div class="invoice-footer">
      <div class="terms">
        <p class="thank-you">Thank You! Visit Again</p>
        <p class="thank-you-tamil">நன்றி! மீண்டும் வாருங்கள்</p>
      </div>

      <div class="signature">
        <p>For {{ invoiceData.business_name }}</p>
        <div class="signature-line"></div>
        <p>Authorized Signatory</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { formatDate } from '../utils/billingUtils.js'
import { amountInWords as toWords, formatPaymentMode } from '@shared/pdf/utils.js'

const props = defineProps({
  invoiceData: {
    type: Object,
    required: true,
  },
  paperSize: {
    type: String,
    default: 'A4',
  },
})

/**
 * Whether any line item has a discount
 */
const hasDiscounts = computed(() => {
  return props.invoiceData?.items?.some((i) => (i.discount_amount || 0) > 0) ?? false
})

/**
 * Format discount display for a line item
 */
const getItemDiscountDisplay = (item) => {
  if (!item.discount_amount || item.discount_amount <= 0) return '-'
  if (item.discount_type === 'percentage') return `${item.discount_value}%`
  if (item.discount_type === 'fixed') return `\u20B9${item.discount_amount.toFixed(2)}`
  return '-'
}

/**
 * Calculate line item total (after item-level discount)
 */
const calculateLineTotal = (item) => {
  return item.quantity * item.unit_price - (item.discount_amount || 0)
}

/**
 * Calculate subtotal (before discounts)
 */
const subtotal = computed(() => {
  if (!props.invoiceData?.items) return 0
  return props.invoiceData.items.reduce((sum, item) => {
    return sum + calculateLineTotal(item)
  }, 0)
})

/**
 * Calculate grand total (after discounts)
 */
const grandTotal = computed(() => {
  const sub = subtotal.value
  const discount = props.invoiceData?.discount_amount || 0
  const loyaltyDiscount = props.invoiceData?.loyalty_discount_amount || 0
  return sub - discount - loyaltyDiscount
})

/**
 * Empty rows count to fill up space
 */
const emptyRowsCount = computed(() => {
  const itemCount = props.invoiceData?.items?.length || 0
  const minRows = 8
  return Math.max(0, minRows - itemCount)
})

/**
 * Amount in words
 */
const amountInWords = computed(() => {
  return toWords(grandTotal.value)
})
</script>

<style scoped>
/* Print-specific styles */
.cash-memo-print {
  width: 210mm;
  min-height: 297mm;
  padding: 15mm;
  margin: 0 auto;
  background: white;
  font-family: 'Arial', sans-serif;
  font-size: 11pt;
  color: #000;
  box-sizing: border-box;
}

/* Header */
.invoice-header {
  text-align: center;
  margin-bottom: 20px;
  border-bottom: 2px solid #000;
  padding-bottom: 10px;
}

.invoice-title {
  font-size: 20pt;
  font-weight: bold;
  margin: 0;
  letter-spacing: 2px;
}

.invoice-title-tamil {
  font-size: 14pt;
  font-weight: bold;
  margin: 5px 0 0 0;
  color: #333;
}

/* Business Details */
.business-details {
  text-align: center;
  margin-bottom: 20px;
  padding: 15px;
  border: 1px solid #000;
}

.business-details h3 {
  font-size: 16pt;
  font-weight: bold;
  margin: 0 0 10px 0;
}

.business-details p {
  margin: 3px 0;
  line-height: 1.4;
}

/* Invoice Info */
.invoice-info {
  margin-bottom: 15px;
  padding: 10px 15px;
  background: #f5f5f5;
  border: 1px solid #000;
}

.invoice-info-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 5px;
}

.invoice-info-row:last-child {
  margin-bottom: 0;
}

/* Items Table */
.items-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 15px;
  border: 1px solid #000;
}

.items-table th,
.items-table td {
  border: 1px solid #000;
  padding: 10px;
  vertical-align: top;
}

.items-table th {
  background: #e0e0e0;
  font-weight: bold;
  font-size: 11pt;
  text-align: left;
}

.items-table td {
  font-size: 11pt;
}

.empty-row td {
  height: 30px;
  border-left: 1px solid #000;
  border-right: 1px solid #000;
  border-bottom: none;
}

.text-center {
  text-align: center;
}

.text-right {
  text-align: right;
}

.product-name {
  font-weight: 500;
}

.tamil-name {
  font-size: 10pt;
  color: #555;
  margin-top: 3px;
}

/* Totals Section */
.totals-section {
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 20px;
  margin-bottom: 20px;
}

.totals-left {
  border: 1px solid #000;
  padding: 15px;
}

.amount-in-words {
  font-size: 11pt;
  line-height: 1.8;
}

.totals-right {
  border: 1px solid #000;
  padding: 15px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.total-row {
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding: 5px 0;
}

.total-row.subtotal {
  font-size: 11pt;
}

.total-row.discount {
  font-size: 11pt;
  color: #c00;
}

.total-row.grand-total {
  font-size: 14pt;
  padding: 10px;
  background: #f5f5f5;
  margin-top: 10px;
  border-top: 1px solid #000;
}

.loyalty-info {
  margin-top: 15px;
  padding: 10px;
  background: #f0f8ff;
  border: 1px dashed #0066cc;
  border-radius: 4px;
  font-size: 10pt;
}

/* Footer */
.invoice-footer {
  display: grid;
  grid-template-columns: 1fr 250px;
  gap: 20px;
  margin-top: 30px;
  border-top: 1px solid #000;
  padding-top: 20px;
}

.terms {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.thank-you {
  font-size: 14pt;
  font-weight: bold;
  margin: 0 0 5px 0;
}

.thank-you-tamil {
  font-size: 12pt;
  font-weight: bold;
  margin: 0;
  color: #333;
}

.signature {
  text-align: right;
}

.signature p {
  margin: 5px 0;
  font-size: 10pt;
}

.signature-line {
  width: 200px;
  border-bottom: 1px solid #000;
  margin: 50px 0 10px auto;
}

/* Print styles */
@media print {
  .cash-memo-print {
    width: 100%;
    margin: 0;
    padding: 10mm;
  }

  /* @page size is injected dynamically via Teleport based on paperSize prop */

  /* A5 print overrides */
  .cash-memo-print.paper-a5 {
    padding: 6mm;
  }
}

/* ─── A5 Layout Overrides ────────────────────────────────── */
.cash-memo-print.paper-a5 {
  width: 148mm;
  min-height: 210mm;
  padding: 10mm;
  font-size: 9.5pt;
}

.cash-memo-print.paper-a5 .invoice-title {
  font-size: 17pt;
}

.cash-memo-print.paper-a5 .invoice-title-tamil {
  font-size: 12pt;
}

.cash-memo-print.paper-a5 .business-details h3 {
  font-size: 13pt;
}

.cash-memo-print.paper-a5 .business-details {
  padding: 10px;
  margin-bottom: 12px;
}

.cash-memo-print.paper-a5 .invoice-info {
  padding: 8px 10px;
  margin-bottom: 10px;
}

.cash-memo-print.paper-a5 .items-table th,
.cash-memo-print.paper-a5 .items-table td {
  padding: 6px;
  font-size: 9.5pt;
}

.cash-memo-print.paper-a5 .totals-section {
  grid-template-columns: 1fr 220px;
  gap: 12px;
  margin-bottom: 12px;
}

.cash-memo-print.paper-a5 .totals-left,
.cash-memo-print.paper-a5 .totals-right {
  padding: 10px;
}

.cash-memo-print.paper-a5 .amount-in-words {
  font-size: 9.5pt;
}

.cash-memo-print.paper-a5 .total-row.grand-total {
  font-size: 12pt;
}

.cash-memo-print.paper-a5 .invoice-footer {
  grid-template-columns: 1fr 180px;
  gap: 12px;
  margin-top: 20px;
  padding-top: 12px;
}

.cash-memo-print.paper-a5 .thank-you {
  font-size: 12pt;
}

.cash-memo-print.paper-a5 .thank-you-tamil {
  font-size: 10.5pt;
}
</style>
